import React from 'react';
import StackedBarChart from '../../../../../components/Charts/StackedBarChart';
import CompositeBarsCharts from '../../../../../components/Charts/CompositeBarsCharts';
import PieCharts from '../../../../../components/Charts/PieCharts';
import LineCharts from '../../../../../components/Charts/LineCharts';
import BarChartt from '../../../../../components/Charts/BarChart';
import CustomMap from '../../../../../components/Maps/Maps';
import HeatMap from '../../../../../components/Maps/HeatMap';
import MultipleYAxesChart from '../../../../../components/Charts/MultipleYAxesChart';
import { BaseMap, CapaMapa, ChartData, Filtro, GraphicsDomain, Variable_indicator } from '../../../../../interfaces/Indicator';
import { CHART_TYPES, ChartTypeValue, isMapType } from '../../../../../utils/chartTypes';

interface TypeOfGraphsProps {
    chartId: number;
    typeOfGraph: ChartTypeValue;
    chartTypeLeftId?: ChartTypeValue;
    data: ChartData[] | BaseMap[];
    chartRef: React.RefObject<HTMLDivElement>;
    typeOfIndicator: string;
    variable_analisis: string[];
    variable_analisis_axis_y_right?: Variable_indicator | null;
    domainAxisYLeft?: GraphicsDomain | null;
    domainAxisYRight?: GraphicsDomain | null;
    errorsNoData: boolean;
    filters: Filtro[];
    scale?: 0 | 1 | null;
    tooltip_indicadores_proc?: string[];
    // -> capas de mapas
    mapLayer?: CapaMapa[];
    activeLayersIds?: number[];
    //Información adicional mapas
    additionalInfoMaps: Record<string, number | string>[];
    //Información dinámica de mapas de calor (encabezado/anio/variable), ya filtrada según los filtros seleccionados
    dynamicInfoMaps?: Record<string, string>[];
    // vertical 0 | horizontal 1
    orientation: 0 | 1 | null;
}

const ChartWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className='w-full h-[328px] md:h-[438px] pt-8 relative'>
        {children}
    </div>
);

const TypeOfGraphs: React.FC<TypeOfGraphsProps> = ({
    chartId,
    typeOfGraph,
    chartTypeLeftId,
    data,
    chartRef,
    typeOfIndicator,
    variable_analisis,
    variable_analisis_axis_y_right,
    domainAxisYLeft,
    domainAxisYRight,
    errorsNoData,
    scale,
    filters,
    tooltip_indicadores_proc,
    mapLayer,
    activeLayersIds,
    additionalInfoMaps,
    dynamicInfoMaps,
    orientation
}) => {

    const isMap = isMapType(typeOfGraph);
    const mapData = isMap ? (data as BaseMap[]) : undefined;
    const chartData = !isMap ? (data as ChartData[]) : undefined;

    switch (typeOfGraph) {
        case CHART_TYPES.GRAFICO_BARRA:
            return (
                <ChartWrapper key={chartId}>
                    <BarChartt
                        data={chartData!}
                        typeOfIndicador={typeOfIndicator}
                        variable_analisis={variable_analisis}
                        ref={chartRef}
                        range={domainAxisYLeft}
                        errorsNoData={errorsNoData}
                        filters={filters}
                        tooltip_indicadores_proc={tooltip_indicadores_proc}
                        orientation={orientation}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.GRAFICO_BARRA_APILABLE:
            return (
                <ChartWrapper key={chartId}>
                    <StackedBarChart
                        data={chartData!}
                        ref={chartRef}
                        variable_analisis={variable_analisis}
                        range={domainAxisYLeft}
                        errorsNoData={errorsNoData}
                        filters={filters}
                        orientation={orientation}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.GRAFICO_BARRA_AGRUPADA:
            return (
                <ChartWrapper key={chartId}>
                    <CompositeBarsCharts
                        data={chartData!}
                        variable_analisis={variable_analisis}
                        ref={chartRef}
                        range={domainAxisYLeft}
                        errorsNoData={errorsNoData}
                        filters={filters}
                        orientation={orientation}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.GRAFICO_TORTA:
            return (
                <ChartWrapper key={chartId}>
                    <PieCharts
                        data={chartData!}
                        ref={chartRef}
                        errorsNoData={errorsNoData}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.GRAFICO_LINEAL:
            return (
                <ChartWrapper key={chartId}>
                    <LineCharts
                        data={chartData!}
                        typeOfIndicador={typeOfIndicator}
                        variable_analisis={variable_analisis}
                        ref={chartRef}
                        range={domainAxisYLeft}
                        errorsNoData={errorsNoData}
                        filters={filters}
                        tooltip_indicadores_proc={tooltip_indicadores_proc}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.GRAFICO_DOBLE_EJE_Y:
            return (
                <ChartWrapper key={chartId}>
                    <MultipleYAxesChart
                        data={chartData!}
                        chartTypeLeft={chartTypeLeftId}
                        variable_analisis_axis_y_left={variable_analisis}
                        variable_analisis_axis_y_right={variable_analisis_axis_y_right}
                        rangeAxisYleft={domainAxisYLeft}
                        rangeAxisYRight={domainAxisYRight}
                        errorsNoData={errorsNoData}
                        ref={chartRef}
                        filters={filters}
                    />
                </ChartWrapper>
            );

        case CHART_TYPES.MAPA:
            return (
                <CustomMap
                    key={chartId}
                    data={mapData!}
                    mapRef={chartRef}
                    errorsNoData={errorsNoData}
                    chartId={chartId}
                    mapLayer={mapLayer}
                    activeCapaIds={activeLayersIds}
                />
            );

        case CHART_TYPES.MAPA_CALOR:
            return (
                <HeatMap
                    key={chartId}
                    data={mapData!}
                    mapRef={chartRef}
                    errorsNoData={errorsNoData}
                    scale={scale}
                    chartId={chartId}
                    mapLayer={mapLayer}
                    activeCapaIds={activeLayersIds}
                    additionalInfo={additionalInfoMaps}
                    dynamicInfo={dynamicInfoMaps}
                />
            );

        default:
            return null;
    }
};

export default TypeOfGraphs;